/**
 * テーブル関連のヘルパー関数
 */

/**
 * ヘッダーkeyから月番号を抽出するユーティリティ
 * @param headerKey - 例: 'transaction-0-amount' または 'transaction--1-amount'
 * @returns 月番号（負の値も含む）
 * @example
 * ```typescript
 * getMonthFromHeaderKey('transaction-0-amount');  // 0
 * getMonthFromHeaderKey('transaction--1-amount'); // -1
 * getMonthFromHeaderKey('transaction-12-amount'); // 12
 * ```
 */
export const getMonthFromHeaderKey = (headerKey: string): number => {
  const match = headerKey.match(/^transaction-(-?\d+)-/);
  return match ? Number(match[1]) : 0;
};

/**
 * 数値を安全に文字列に変換する
 * @param value - 変換対象の数値（undefined可能）
 * @param defaultValue - デフォルト値（デフォルト: '--'）
 * @returns 変換された文字列
 * @example
 * ```typescript
 * safeNumberToString(123);      // '123'
 * safeNumberToString(undefined); // '--'
 * safeNumberToString(null, '0'); // '0'
 * ```
 */
export const safeNumberToString = (value: number | undefined | null, defaultValue = '--'): string => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  return value.toString();
};

/**
 * 数値が負の値かどうかを判定する（useTableStateから移行検討）
 * @param value - 判定対象の数値
 * @returns 負の値の場合true、それ以外false
 * @deprecated useTableState.isNegative を使用してください
 */
export const isNegative = (value?: number): boolean => {
  return value !== undefined && value < 0;
};

/**
 * CSS クラス名を安全に結合する
 * @param classes - 結合するクラス名の配列（undefined/null含む）
 * @returns 結合されたクラス名文字列
 * @example
 * ```typescript
 * joinClasses(['class1', 'class2', undefined, 'class3']); // 'class1 class2 class3'
 * joinClasses([]);                                        // ''
 * ```
 */
export const joinClasses = (...classes: (string | undefined | null)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * DOM要素の安全な取得
 * @param id - 要素のID
 * @returns DOM要素（見つからない場合はnull）
 * @example
 * ```typescript
 * const element = safeGetElementById('table-container');
 * if (element) {
 *   // 要素が存在する場合の処理
 * }
 * ```
 */
export const safeGetElementById = (id: string): HTMLElement | null => {
  return document.getElementById(id);
};
